import { LocaleLink } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import { ArrowRight, MapPin, Heart, Lightbulb, Target } from "lucide-react";
import founderPhoto from "@/assets/alexander-wolfshohl.jpg";
import chrisPhoto from "@/assets/chris-krueglstein.png";
import veronikaPhoto from "@/assets/veronika-greilich.png";
import Brand from "@/components/ui/brand";
import WhatIsCraftfindus from "@/components/seo/WhatIsCraftfindus";
import dukePhoto from "@/assets/duke.jpg";
import heroAboutBg from "@/assets/hero-about-bg.jpg";
import { useTranslation } from "react-i18next";

const UeberUns = () => {
  const { t } = useTranslation();

  const values = [
    { icon: Target, title: t("about.value1Title"), description: t("about.value1Desc") },
    { icon: Heart, title: t("about.value2Title"), description: t("about.value2Desc") },
    { icon: Lightbulb, title: t("about.value3Title"), description: t("about.value3Desc") },
  ];

  return (
    <div className="min-h-screen bg-background" id="main-content">
      <SEOHead
        title={t("about.seoTitle")}
        description={t("about.seoDesc")}
        canonical="/ueber-uns"
      />
      <JsonLd
        includeLocalBusiness
        breadcrumbs={[
          { name: t("common.home", { defaultValue: "Startseite" }), url: "https://www.craftfindus.com/" },
          { name: t("footer.aboutUs"), url: "https://www.craftfindus.com/ueber-uns" },
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden" aria-label={t("about.heroLabel", { brand: "CRAFTFINDUS" })}>
        <div className="absolute inset-0">
          <img src={heroAboutBg} alt={t("about.seoDesc")} className="w-full h-full object-cover" loading="eager" fetchPriority="high" width={1920} height={1080} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 section-padding pb-20 md:pb-24 pt-24 md:pt-32 w-full">
          <div className="max-w-4xl">
            <span className="text-micro tracking-[0.2em] opacity-0 animate-fade-up">
              {t("about.heroLabel", { brand: "CRAFTFINDUS" })}
            </span>
            <h1 className="text-display mt-6 opacity-0 animate-fade-up delay-100">
              {t("about.heroTitle1")}<br />
              <span className="whitespace-nowrap">CRAFT<span className="text-accent">FINDUS</span>.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mt-8 leading-relaxed opacity-0 animate-fade-up delay-200">
              {t("about.heroDesc", { brand: "CRAFTFINDUS" })}
            </p>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 opacity-0 animate-fade-up delay-500 hidden md:block">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-muted-foreground/40 mx-auto" />
        </div>
      </section>

      {/* Team */}
      <section className="section-padding py-20 border-t border-border">
        <div className="max-w-5xl mx-auto">
          <span className="text-micro tracking-[0.2em]">{t("about.teamLabel")}</span>
          <h2 className="text-headline mt-4 mb-8">{t("about.teamTitle")} <span className="whitespace-nowrap"><span className="text-foreground">CRAFT</span><span className="text-accent">FINDUS</span></span></h2>

          {/* Alexander */}
          <div className="flex flex-col md:flex-row gap-10 items-start">
            <img src={founderPhoto} alt="Alexander Wolfshohl" className="w-40 h-40 rounded-full object-cover border-2 border-accent/20 shrink-0 transition-transform duration-500 hover:scale-105" />
            <div className="space-y-6">
              <h3 className="text-subheadline">Alexander Wolfshohl | {t("about.founderRole")}</h3>
              <p className="text-body leading-relaxed max-w-3xl">{t("about.founderBio1")}</p>
              <p className="text-caption leading-relaxed max-w-3xl">{t("about.founderBio2", { brand: "CRAFTFINDUS" })}</p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                <MapPin className="w-4 h-4 text-accent" />
                Solingen, Nordrhein-Westfalen
              </div>
            </div>
          </div>

          {/* Chris */}
          <div className="flex flex-col md:flex-row gap-10 items-start mt-16">
            <img src={chrisPhoto} alt="Chris Krueglstein" className="w-40 h-40 rounded-full object-cover border-2 border-accent/20 shrink-0 transition-transform duration-500 hover:scale-105" />
            <div className="space-y-4">
              <h3 className="text-subheadline">Chris Krueglstein | {t("about.chrisRole")}</h3>
              <p className="text-caption leading-relaxed max-w-3xl">{t("about.chrisBio", { brand: "CRAFTFINDUS" })}</p>
            </div>
          </div>

          {/* Veronika */}
          <div className="flex flex-col md:flex-row gap-10 items-start mt-16">
            <img src={veronikaPhoto} alt="Veronika Greilich" className="w-40 h-40 rounded-full object-cover border-2 border-accent/20 shrink-0 transition-transform duration-500 hover:scale-105" />
            <div className="space-y-4">
              <h3 className="text-subheadline">Veronika Greilich | {t("about.veronikaRole")}</h3>
              <p className="text-caption leading-relaxed max-w-3xl">{t("about.veronikaBio")}</p>
            </div>
          </div>

          {/* Duke */}
          <div className="flex flex-col md:flex-row gap-10 items-start mt-16">
            <img src={dukePhoto} alt="Duke" className="w-40 h-40 rounded-full object-cover border-2 border-accent/20 shrink-0 transition-transform duration-500 hover:scale-105" />
            <div className="space-y-4">
              <h3 className="text-subheadline">Duke | {t("about.dukeRole")}</h3>
              <p className="text-caption leading-relaxed max-w-3xl">{t("about.dukeBio")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding py-20 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <span className="text-micro tracking-[0.2em]">{t("about.valuesLabel")}</span>
          <h2 className="text-headline mt-4 mb-12">{t("about.valuesTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v) => (
              <div key={v.title} className="opportunity-card space-y-4">
                <v.icon className="w-6 h-6 text-accent" />
                <h3 className="text-subheadline">{v.title}</h3>
                <p className="text-caption leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding py-20 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <span className="text-micro tracking-[0.2em]">{t("about.missionLabel")}</span>
          <h2 className="text-headline mt-4 mb-8">{t("about.missionTitle")}</h2>
          <div className="space-y-5 text-caption leading-relaxed">
            <p>{t("about.missionP1")}</p>
            <p>{t("about.missionP2", { brand: "CRAFTFINDUS" })}</p>
            <p>{t("about.missionP3")}</p>
          </div>
        </div>
      </section>

      {/* AI Search: Was ist CRAFTFINDUS? */}
      <WhatIsCraftfindus />

      {/* CTA */}
      <section className="section-padding py-20 border-t border-border text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-headline">{t("about.ctaTitle")}</h2>
          <p className="text-caption leading-relaxed">{t("about.ctaDesc")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
            <LocaleLink to="/inquiry">
              <Button variant="commit" size="lg" className="group">
                {t("about.ctaInquiry")}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </LocaleLink>
            <LocaleLink to="/kontakt">
              <Button variant="outline" size="lg">{t("about.ctaContact")}</Button>
            </LocaleLink>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UeberUns;
